import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import type { PublicUser } from '@gitrader/shared';
import { ApiError, api } from './api';

interface SessionValue {
  user: PublicUser | null;
  needsBootstrap: boolean;
  isLoading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const setup = useQuery({
    queryKey: ['setup-status'],
    queryFn: api.setupStatus,
    staleTime: 30_000,
  });

  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return (await api.me()).user;
      } catch (error) {
        // 401 is the normal "signed out" answer, not a failure worth retrying.
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 30_000,
  });

  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['me'] }),
      queryClient.invalidateQueries({ queryKey: ['setup-status'] }),
    ]);
  }, [queryClient]);

  const signOut = useCallback(async () => {
    await api.logout();
    queryClient.setQueryData(['me'], null);
    await refresh();
  }, [queryClient, refresh]);

  const value = useMemo<SessionValue>(
    () => ({
      user: me.data ?? null,
      needsBootstrap: setup.data?.needsBootstrap ?? false,
      isLoading: me.isLoading || setup.isLoading,
      refresh,
      signOut,
    }),
    [me.data, me.isLoading, setup.data, setup.isLoading, refresh, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** A fixed, already-resolved session for component tests — no network, no bootstrap query. */
export function SessionContextForTests({
  user,
  children,
}: {
  user: PublicUser | null;
  children: ReactNode;
}) {
  const value = useMemo<SessionValue>(
    () => ({
      user,
      needsBootstrap: false,
      isLoading: false,
      refresh: async () => {},
      signOut: async () => {},
    }),
    [user],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}
